/**
 * Transition registry — route-pair resolution with dynamic imports.
 *
 * Maps source:destination namespace pairs to transition modules.
 * Resolves in priority order: exact → *:to → from:* → *:*
 * Transition modules are loaded on demand to keep the critical path small.
 *
 * @see specs/002-swup-view-transitions/contracts/router-api.md
 */

/**
 * Route-pair → transition module path map.
 *
 * Keys use "from:to" format where namespaces come from body[data-namespace].
 * Wildcard (*) matches any namespace.
 *
 * Add new entries here to assign transitions to specific route pairs.
 */
const transitionMap = {
    'work:projects/show': 'flip-expand',
    'work:*':             'stagger',
    '*:home':             'reveal',
    '*:*':                'crossfade',
};

/**
 * Module cache — avoids re-importing the same transition module.
 */
const moduleCache = new Map();

/**
 * Dynamically import a transition module by name.
 * Modules live in resources/js/transitions/{name}.js
 *
 * @param {string} name - Transition module name (e.g. 'crossfade', 'slide')
 * @returns {Promise<{out: Function, in: Function}>}
 */
function loadTransition(name) {
    if (moduleCache.has(name)) {
        return moduleCache.get(name);
    }

    const promise = import(`../transitions/${name}.js`);
    moduleCache.set(name, promise);
    return promise;
}

/**
 * Resolve the transition name for a visit based on route-pair matching.
 *
 * Resolution order:
 * 1. Exact match: "from:to"
 * 2. Wildcard destination: "*:to"
 * 3. Wildcard source: "from:*"
 * 4. Default: "*:*" (always crossfade)
 *
 * @param {string} from - Source namespace
 * @param {string} to - Destination namespace
 * @returns {string} Transition name
 */
function resolveTransitionName(from, to) {
    const exact = `${from}:${to}`;
    if (transitionMap[exact]) return transitionMap[exact];

    const wildcardTo = `*:${to}`;
    if (transitionMap[wildcardTo]) return transitionMap[wildcardTo];

    const wildcardFrom = `${from}:*`;
    if (transitionMap[wildcardFrom]) return transitionMap[wildcardFrom];

    return transitionMap['*:*'];
}

/**
 * Resolve the transition for a Swup visit.
 *
 * Sets visit.animation.name to the resolved transition name,
 * then returns the dynamically imported transition module.
 * Used as the JsPlugin transitions handler.
 *
 * @param {Object} visit - Swup visit object
 * @returns {Promise<{out: Function, in: Function}>}
 */
export function resolveTransition(visit) {
    const from = visit.from?.dataset?.namespace || '*';
    const to = visit.to?.dataset?.namespace || '*';

    const name = resolveTransitionName(from, to);
    visit.animation.name = name;

    return loadTransition(name);
}
